import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Loader2, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BONE = '#f5e8c7';
const GOLD = '#c8a672';

// Netflix-themed return page after Stripe hosted checkout. Polls the SAME
// /api/payments/status endpoint the legacy page used — Stripe webhooks are
// what actually update subscription_status in the DB, so this screen is
// cosmetic + confirmation only. On success we refresh the auth context so
// the user chip in the top nav flips to "Subscriber" immediately, then
// bounce back to the Netflix home.
const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) pollPaymentStatus(sessionId);
    else setStatus('error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    if (attempts >= 6) {
      setStatus('timeout');
      return;
    }
    try {
      const resp = await axios.get(`${API_URL}/api/payments/status/${sid}`);
      if (resp.data?.payment_status === 'paid') {
        setStatus('success');
        await refreshUser();
        // Give the user a beat to see the confirmation, then return home.
        setTimeout(() => navigate('/', { replace: true }), 2200);
      } else if (resp.data?.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
      }
    } catch (e) {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0a0705' }}>
      <div
        className="w-full max-w-md rounded-2xl border p-10 text-center"
        style={{ background: '#141010', borderColor: `${GOLD}66` }}
      >
        {status === 'checking' && (
          <>
            <div
              className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
              style={{ background: `${GOLD}22`, color: GOLD }}
            >
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
            <h2 className="font-display uppercase text-2xl mb-3" style={{ color: BONE }}>
              Confirming your payment
            </h2>
            <p className="text-sm text-white/60">
              Hang tight — Stripe is talking to our server. This usually takes a few seconds.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div
              className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
              style={{ background: `${GOLD}22`, color: GOLD }}
            >
              <CheckCircle className="w-8 h-8" />
            </div>
            <h2 className="font-display uppercase text-2xl mb-3" style={{ color: BONE }}>
              You&apos;re in
            </h2>
            <p className="text-sm text-white/70 mb-6">
              Your subscription is live. Monthly credits, event replays, and photo galleries are unlocked.
            </p>
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 min-h-[44px] rounded-md font-semibold transition-all hover:scale-[1.02]"
              style={{ background: GOLD, color: '#0a0705' }}
              data-testid="payment-success-home"
            >
              Back to Elevated
            </button>
            <p className="mt-4 text-xs text-white/40">Returning you home automatically…</p>
          </>
        )}

        {status !== 'checking' && status !== 'success' && (
          <>
            <div
              className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
              style={{ background: 'rgba(248,113,113,0.15)', color: '#f87171' }}
            >
              <AlertTriangle className="w-8 h-8" />
            </div>
            <h2 className="font-display uppercase text-2xl mb-3" style={{ color: BONE }}>
              Payment pending
            </h2>
            <p className="text-sm text-white/70 mb-6">
              We couldn&apos;t confirm the payment yet. If you were charged, your access will unlock
              within a minute via our Stripe webhook — refresh the home page in a moment.
            </p>
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 min-h-[44px] rounded-md font-semibold transition-all hover:scale-[1.02]"
              style={{ background: BONE, color: '#0a0705' }}
              data-testid="payment-success-home-fallback"
            >
              Back to Elevated
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
